import React, { useEffect, useState } from 'react';
import { 
  Avatar, 
  Button, 
  Tooltip, 
  Dropdown, 
  Space, 
  Typography,
  Badge,
  Divider,
  Drawer
} from 'antd';
import { 
  LogoutOutlined, 
  UsergroupAddOutlined, 
  MenuOutlined, 
  CloseOutlined, 
  LayoutOutlined,
  UserOutlined,
  TeamOutlined,
  SettingOutlined,
  BellOutlined,
  ArrowRightOutlined,
  DashboardOutlined,
  PhoneOutlined,
  MailOutlined
} from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';

// Import your existing images
import logoCollapsed from '../../img/sidebar/logoCollapsed.png';
import logoExpanded from '../../img/sidebar/logoExpanded.png';
import demoUser from '../../img/sidebar/avatar.png';
import provider from '../../img/sidebar/provider.png';

const { Text } = Typography;

// Enhanced color palette matching your theme
const colors = {
  primary: '#00796b',
  secondary: '#004d40',
  success: '#52c41a',
  warning: '#faad14',
  error: '#ff4d4f',
  info: '#1677ff',
  gradients: {
    primary: 'linear-gradient(135deg, #00796b 0%, #004d40 100%)',
    success: 'linear-gradient(135deg, #52c41a 0%, #389e0d 100%)',
    error: 'linear-gradient(135deg, #ff4d4f 0%, #cf1322 100%)',
    info: 'linear-gradient(135deg, #1677ff 0%, #0050b3 100%)',
    soft: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
    glass: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%)',
    aurora: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  },
  neutral: {
    50: '#FAFAFA',
    100: '#F5F5F5',
    200: '#E8E8E8',
    300: '#D9D9D9',
    400: '#BFBFBF',
    500: '#8C8C8C',
    600: '#595959',
    700: '#434343',
    800: '#262626',
    900: '#1F1F1F'
  }
};

// Enhanced mobile styles
const mobileStyles = `
  @keyframes slideInFromLeft {
    from {
      transform: translateX(-100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }

  @keyframes slideInFromRight {
    from {
      transform: translateX(20px);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }

  @keyframes fadeInUp {
    from {
      transform: translateY(20px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }

  @keyframes glow {
    0%, 100% {
      box-shadow: 0 0 5px rgba(0, 121, 107, 0.3);
    }
    50% {
      box-shadow: 0 0 20px rgba(0, 121, 107, 0.6), 0 0 30px rgba(0, 121, 107, 0.4);
    }
  }

  @keyframes pulse {
    0%, 100% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.05);
    }
  }

  @keyframes ripple {
    0% {
      transform: scale(0);
      opacity: 1;
    }
    100% {
      transform: scale(4);
      opacity: 0;
    }
  }

  .mobile-header {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 1001;
    height: 70px;
    background: linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.95) 100%);
    backdrop-filter: blur(20px);
    border-bottom: 1px solid rgba(0, 121, 107, 0.1);
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
  }

  .mobile-menu-button {
    position: relative;
    overflow: hidden;
    background: linear-gradient(135deg, rgba(0, 121, 107, 0.1) 0%, rgba(0, 77, 64, 0.1) 100%);
    border: 1px solid rgba(0, 121, 107, 0.2);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .mobile-menu-button::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 0;
    height: 0;
    background: rgba(0, 121, 107, 0.2);
    border-radius: 50%;
    transition: all 0.3s ease;
    transform: translate(-50%, -50%);
  }

  .mobile-menu-button:active::before {
    width: 100px;
    height: 100px;
    animation: ripple 0.6s ease-out;
  }

  .mobile-logo {
    background: linear-gradient(135deg, #00796b 0%, #004d40 100%);
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    position: relative;
  }

  .mobile-logo::after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(135deg, #00796b 0%, #004d40 100%);
    border-radius: 1px;
  }

  .drawer-content {
    background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  }

  .drawer-header {
    background: linear-gradient(135deg, rgba(0, 121, 107, 0.02) 0%, rgba(0, 77, 64, 0.02) 100%);
    border-bottom: 1px solid rgba(0, 121, 107, 0.1);
    position: relative;
    overflow: hidden;
  }

  .drawer-header::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(135deg, #00796b 0%, #004d40 100%);
  }

  .nav-item-mobile {
    position: relative;
    margin: 8px 16px;
    border-radius: 16px;
    overflow: hidden;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    background: linear-gradient(135deg, rgba(255,255,255,0.8) 0%, rgba(248,250,252,0.8) 100%);
    border: 1px solid rgba(0, 121, 107, 0.05);
    backdrop-filter: blur(10px);
  }

  .nav-item-mobile::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent);
    transition: left 0.5s;
  }

  .nav-item-mobile:hover::before {
    left: 100%;
  }

  .nav-item-mobile:active {
    transform: scale(0.98);
  }

  .nav-item-mobile.active {
    background: linear-gradient(135deg, #00796b 0%, #004d40 100%);
    border: 1px solid rgba(0, 121, 107, 0.3);
    box-shadow: 0 8px 32px rgba(0, 121, 107, 0.25);
    animation: glow 2s ease-in-out infinite;
  }

  .nav-icon-mobile {
    width: 24px;
    height: 24px;
    object-fit: contain;
    transition: all 0.3s ease;
  }

  .nav-item-mobile:not(.active) .nav-icon-mobile {
    filter: brightness(0.7);
  }

  .nav-item-mobile.active .nav-icon-mobile {
    filter: brightness(0) invert(1);
  }

  .user-section-mobile {
    background: linear-gradient(135deg, rgba(0, 121, 107, 0.05) 0%, rgba(0, 77, 64, 0.05) 100%);
    border-top: 1px solid rgba(0, 121, 107, 0.1);
    position: relative;
    overflow: hidden;
  }

  .user-section-mobile::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(135deg, #00796b 0%, #004d40 100%);
  }

  .user-avatar-mobile {
    position: relative;
    border: 3px solid rgba(0, 121, 107, 0.2);
    box-shadow: 0 8px 24px rgba(0, 121, 107, 0.15);
    transition: all 0.3s ease;
  }

  .user-avatar-mobile::after {
    content: '';
    position: absolute;
    top: -3px;
    left: -3px;
    right: -3px;
    bottom: -3px;
    border-radius: 50%;
    background: linear-gradient(135deg, #00796b 0%, #004d40 100%);
    z-index: -1;
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  .user-avatar-mobile:hover::after {
    opacity: 1;
    animation: pulse 2s infinite;
  }

  .action-button-mobile {
    background: linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(248,250,252,0.9) 100%);
    border: 1px solid rgba(0, 121, 107, 0.15);
    backdrop-filter: blur(10px);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    overflow: hidden;
  }

  .action-button-mobile::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 0;
    height: 0;
    background: rgba(0, 121, 107, 0.1);
    border-radius: 50%;
    transition: all 0.3s ease;
    transform: translate(-50%, -50%);
  }

  .action-button-mobile:hover::before {
    width: 100%;
    height: 100%;
  }

  .action-button-mobile:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0, 121, 107, 0.15);
    border-color: rgba(0, 121, 107, 0.3);
  }

  .action-button-mobile.danger {
    border-color: rgba(255, 77, 79, 0.15);
  }

  .action-button-mobile.danger:hover {
    border-color: rgba(255, 77, 79, 0.3);
    box-shadow: 0 8px 24px rgba(255, 77, 79, 0.15);
  }

  .action-button-mobile.danger::before {
    background: rgba(255, 77, 79, 0.1);
  }

  .section-title-mobile {
    position: relative;
    padding: 16px 20px 8px;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 1.5px;
    color: rgba(0, 121, 107, 0.8);
    background: linear-gradient(135deg, rgba(0, 121, 107, 0.05) 0%, transparent 100%);
  }

  .section-title-mobile::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 20px;
    right: 20px;
    height: 1px;
    background: linear-gradient(135deg, rgba(0, 121, 107, 0.3) 0%, transparent 100%);
  }

  .notification-dot {
    position: absolute;
    top: 8px;
    right: 8px;
    width: 8px;
    height: 8px;
    background: linear-gradient(135deg, #ff4d4f 0%, #cf1322 100%);
    border-radius: 50%;
    border: 2px solid white;
    box-shadow: 0 2px 8px rgba(255, 77, 79, 0.3);
    animation: pulse 2s infinite;
  }
`;

const EnhancedSideBarMobile = () => {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState('providers');
  const navigate = useNavigate();
  const location = useLocation();

  // Inject enhanced styles
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = mobileStyles;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  useEffect(() => {
    setCurrent(location.pathname.split('/')[1]);
  }, [location]);

  // Mock user data (replace with actual data from localStorage)
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isRelationshipManager = user?.team_role?.[0]?.role === 'Relationship_Manager';
  const isSuperAdmin = user?.team_role?.some(tr => tr.role === 'Super_Admin');

  const SPOnboarding = isRelationshipManager ? [
    {
      key: 'assigned-providers',
      title: 'Provider Onboarding',
      link: '/assigned-providers',
      icon: provider,
      visible: true,
      hasNotification: true
    },
  ] : [
    {
      key: 'providers',
      title: 'Provider Onboarding',
      link: '/providers',
      icon: provider,
      visible: true,
      hasNotification: false
    },
  ];

  const logOut = () => {
    console.log('Logging out...');
    localStorage.clear();
    navigate('/login');
    setOpen(false);
  };

  const handleNavigation = (path) => {
    console.log('Navigate to:', path);
    navigate(path);
    setOpen(false); // Close drawer after navigation
  };

  const renderNavItem = (item) => {
    const isActive = current === item.key;
    
    return (
      <div
        key={item.key}
        className={`nav-item-mobile ${isActive ? 'active' : ''}`}
        onClick={() => {
          setCurrent(item.key);
          handleNavigation(item.link);
        }}
        style={{
          animation: `slideInFromRight 0.3s ease-out ${0.1}s both`
        }}
      >
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          padding: '18px 20px',
          position: 'relative'
        }}>
          <div style={{ position: 'relative' }}>
            <img 
              src={item.icon}
              className="nav-icon-mobile"
              alt={item.title}
            />
            {item.hasNotification && (
              <div className="notification-dot" />
            )}
          </div>
          <div style={{ flex: 1 }}>
            <Text style={{ 
              fontSize: '16px',
              fontWeight: '600',
              color: isActive ? '#FFFFFF' : colors.neutral[700],
              display: 'block',
              marginBottom: '2px'
            }}>
              {item.title}
            </Text>
            <Text style={{ 
              fontSize: '13px',
              color: isActive ? 'rgba(255,255,255,0.8)' : colors.neutral[500]
            }}>
              Manage provider data
            </Text>
          </div>
          <ArrowRightOutlined 
            style={{ 
              fontSize: '14px',
              color: isActive ? 'rgba(255,255,255,0.8)' : colors.neutral[400],
              transition: 'all 0.3s ease'
            }} 
          />
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Enhanced Mobile Header */}
      <div className="mobile-header">
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 20px',
          height: '100%'
        }}>
          <Button
            className="mobile-menu-button"
            type="text"
            icon={<MenuOutlined style={{ fontSize: '20px', color: colors.primary }} />}
            onClick={() => setOpen(true)}
            style={{
              width: '48px',
              height: '48px',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          />

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <img 
              src={logoCollapsed} 
              style={{ 
                height: '32px',
                width: 'auto',
                filter: 'drop-shadow(0 2px 8px rgba(0, 121, 107, 0.3))'
              }} 
              alt="Logo"
            />
            <div className="mobile-logo" style={{
              fontWeight: '800',
              fontSize: '20px',
              letterSpacing: '1px'
            }}>
              EDEN
            </div>
          </div>

          <div style={{ 
            width: '48px',
            height: '48px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Badge dot>
              <BellOutlined style={{ 
                fontSize: '20px', 
                color: colors.neutral[600],
                cursor: 'pointer',
                transition: 'color 0.3s ease'
              }} />
            </Badge>
          </div>
        </div>
      </div>

      {/* Enhanced Mobile Drawer */}
      <Drawer
        title={null}
        placement="left"
        onClose={() => setOpen(false)}
        open={open}
        width={340}
        closable={false}
        styles={{
          body: { 
            padding: 0,
          }
        }}
        className="drawer-content"
      >
        <div style={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          animation: 'slideInFromLeft 0.4s ease-out'
        }}>
          {/* Enhanced Drawer Header */}
          <div className="drawer-header" style={{
            padding: '32px 24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <img 
                src={logoExpanded} 
                style={{ 
                  height: '40px',
                  width: 'auto',
                  filter: 'drop-shadow(0 4px 12px rgba(0, 121, 107, 0.3))'
                }} 
                alt="Logo"
              />
              <div style={{
                background: colors.gradients.primary,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontWeight: '800',
                fontSize: '24px',
                letterSpacing: '1px'
              }}>
                EDEN
              </div>
            </div>
            <Button
              type="text"
              icon={<CloseOutlined style={{ fontSize: '18px' }} />}
              onClick={() => setOpen(false)}
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: colors.neutral[600],
                background: 'rgba(0, 121, 107, 0.05)',
                border: '1px solid rgba(0, 121, 107, 0.1)'
              }}
            />
          </div>

          {/* Enhanced Navigation */}
          <div style={{
            flex: 1,
            paddingTop: '16px',
            paddingBottom: '200px',
            overflowY: 'auto'
          }}>
            <div className="section-title-mobile">
              Navigation Menu
            </div>
            <div style={{ padding: '8px 0' }}>
              {SPOnboarding.filter(item => item.visible).map(renderNavItem)}
            </div>
          </div>

          {/* Enhanced User Section */}
          <div className="user-section-mobile">
            <div style={{
              padding: '24px',
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                marginBottom: '24px',
                animation: 'fadeInUp 0.5s ease-out 0.2s both'
              }}>
                <div className="user-avatar-mobile">
                  <Avatar
                    size={64}
                    src={demoUser}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Text strong style={{ 
                    fontSize: '18px',
                    color: colors.neutral[800],
                    display: 'block',
                    marginBottom: '4px',
                    fontWeight: '700'
                  }}>
                    {user?.name || 'John Smith'}
                  </Text>
                  <Text style={{ 
                    fontSize: '14px',
                    color: colors.neutral[500],
                    display: 'block',
                    marginBottom: '4px'
                  }}>
                    {user?.team_role?.[0]?.role?.replace('_', ' ') || 'Administrator'}
                  </Text>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <div style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      background: colors.gradients.success,
                      animation: 'pulse 2s infinite'
                    }} />
                    <Text style={{ 
                      fontSize: '12px',
                      color: colors.success,
                      fontWeight: '500'
                    }}>
                      Online
                    </Text>
                  </div>
                </div>
              </div>

              {/* Enhanced User Actions */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                animation: 'fadeInUp 0.5s ease-out 0.3s both'
              }}>
                {isSuperAdmin && (
                  <Button
                    className="action-button-mobile"
                    type="text"
                    icon={<UsergroupAddOutlined style={{ color: colors.primary }} />}
                    onClick={() => handleNavigation('/users')}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      gap: '12px',
                      height: '48px',
                      padding: '0 20px',
                      borderRadius: '12px',
                      fontSize: '15px',
                      fontWeight: '600',
                      color: colors.neutral[700],
                      textAlign: 'left'
                    }}
                  >
                    <span>Manage Users</span>
                    <ArrowRightOutlined style={{ 
                      marginLeft: 'auto',
                      fontSize: '12px',
                      color: colors.neutral[400]
                    }} />
                  </Button>
                )}
                
                <Button
                  className="action-button-mobile"
                  type="text"
                  icon={<SettingOutlined style={{ color: colors.neutral[600] }} />}
                  onClick={() => console.log('Settings')}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    gap: '12px',
                    height: '48px',
                    padding: '0 20px',
                    borderRadius: '12px',
                    fontSize: '15px',
                    fontWeight: '600',
                    color: colors.neutral[700],
                    textAlign: 'left'
                  }}
                >
                  <span>Settings & Privacy</span>
                  <ArrowRightOutlined style={{ 
                    marginLeft: 'auto',
                    fontSize: '12px',
                    color: colors.neutral[400]
                  }} />
                </Button>

                <Button
                  className="action-button-mobile"
                  type="text"
                  icon={<PhoneOutlined style={{ color: colors.info }} />}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    gap: '12px',
                    height: '48px',
                    padding: '0 20px',
                    borderRadius: '12px',
                    fontSize: '15px',
                    fontWeight: '600',
                    color: colors.neutral[700],
                    textAlign: 'left'
                  }}
                >
                  <span>Help & Support</span>
                  <ArrowRightOutlined style={{ 
                    marginLeft: 'auto',
                    fontSize: '12px',
                    color: colors.neutral[400]
                  }} />
                </Button>

                <Divider style={{ margin: '16px 0' }} />

                <Button
                  className="action-button-mobile danger"
                  type="text"
                  icon={<LogoutOutlined style={{ color: colors.error }} />}
                  onClick={logOut}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    gap: '12px',
                    height: '48px',
                    padding: '0 20px',
                    borderRadius: '12px',
                    fontSize: '15px',
                    fontWeight: '600',
                    color: colors.error,
                    textAlign: 'left'
                  }}
                >
                  <span>Sign Out</span>
                  <LogoutOutlined style={{ 
                    marginLeft: 'auto',
                    fontSize: '12px'
                  }} />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Drawer>
    </>
  );
};

export default EnhancedSideBarMobile;